import { type DependencyList, type RefObject, useLayoutEffect, useState } from 'react';

/**
 * Distance from the top of a scroll container's content to an element inside
 * it: what a virtualizer calls its `scrollMargin` when the list does not start
 * at the top of the scroller (a page header and filters sit above it).
 *
 * Re-measured whenever `deps` change; pass what can move the element (its
 * width, the list it holds). Returns 0 while the container is unknown.
 */
export const useScrollMargin = (
  ref: RefObject<HTMLElement | null>,
  scrollParent: HTMLElement | null,
  deps: DependencyList
): number => {
  const [margin, setMargin] = useState(0);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !scrollParent) return;

    const next =
      element.getBoundingClientRect().top -
      scrollParent.getBoundingClientRect().top +
      scrollParent.scrollTop;
    // Sub-pixel jitter would otherwise re-render the list for nothing.
    setMargin((current) => (Math.abs(current - next) < 1 ? current : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller names what moves the element
  }, [ref, scrollParent, ...deps]);

  return margin;
};
